import { Offcanvas, Form, Card, Row, Col } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import { quillImageHandler } from "../../utility/quilImageHandler";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}
interface Tag {
  _id: string;
  name: string;
}
type FormData = {
  name: string;
  description: string;
  tagId: string;
  link: string;
  link_name: string;
  publishedAt: string;
  featured?: boolean;
  keywords?: string;
  meta_description?: string;
  slug?: string;
};

const AddMediaList = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue, // ✅ Needed for prefill
    watch,
    trigger,
  } = useForm<FormData>();

  const [value, setEditorValue] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const quillRef = useRef<any>(null);
  // ✅ Tag dropdown states
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;
  const [pdfFile, setPdfFile] = useState<File | string | null>(null);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
  }>({});
  // ✅ Fetch paginated tags
  const fetchTags = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `tags/get-tags/NEWS`, // no pagination params
        body: null,
      });

      const tags: Tag[] = res?.data || [];
      setTagsList(tags);

      // ✅ If editing existing data, auto-select the tag
      if (data?.tagId) {
        const tagExists = tags.some((tag: Tag) => tag._id === data.tagId);
        if (tagExists) setValue("tagId", data.tagId);
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const titleValid = await trigger(["name"]);

    const newErrors: any = {};

    if (isQuillEmpty(value)) {
      newErrors.description = "Description is required";
    }

    setCustomErrors(newErrors);

    // 🔥 IMPORTANT: return actual validity
    return titleValid && !newErrors.description;
  };
const nameValue = watch("name");

useEffect(() => {
  if (data?._id) return; // don't auto-generate slug when editing
  if (!nameValue) {
    setValue("slug", "");
    return;
  }

  const generatedSlug = HelperService.generateSlug(nameValue);

  setValue("slug", generatedSlug, {
    shouldValidate: true,
    shouldDirty: true,
  });

}, [nameValue]);
  // ✅ Load data on open
  useEffect(() => {
    if (show) {
      fetchTags().then(() => {
        reset({
          name: data?.name || "",
          description: data?.description || "",
          tagId: data?.tagId || "",
          link: data?.link || "",
          link_name: data?.link_name || "",
          publishedAt: data?.publishedAt
            ? new Date(data.publishedAt).toISOString().slice(0, 10)
            : "",
          featured: data?.featured || false,
          keywords: data?.keywords || "",
          meta_description: data?.meta_description || "",
          slug: data?.slug || "",
        });
        setEditorValue(data?.description || "");
if (data?.file) setPdfFile(data.file);
      });
    } else {
      reset({});
      setPdfFile(null);
      setEditorValue("");
    }
  }, [show, data]);
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  // ✅ Submit handler
  const onSubmit = async (request: FormData) => {
    if (fileError) {
      toast.error(fileError);
      return;
    }
    if (!file && !data?.image) {
      toast.error("Image is required");
      return;
    }
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append("name", request.name);
     if (!pdfFile && data?._id) {
  formData.append("removePdf", "true");
}
    const editor = quillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html);
formData.append("link", request.link || "");
formData.append("link_name", request.link_name || "");
    formData.append("featured", request.featured ? "true" : "false");
    formData.append("keywords", request.keywords || "");
    formData.append("meta_description", request.meta_description || "");
    formData.append("slug", request.slug || "");
    if (request.tagId) formData.append("tagId", request.tagId);
    if (file) {
      formData.append("image", file);
    }

    if (pdfFile && pdfFile instanceof File) {
      formData.append("file", pdfFile); // only send new pdf file
    }
    if (request.publishedAt) {
      const selectedDate = new Date(request.publishedAt);

      // Convert local datetime to ISO (for consistent DB storage)
      const now = new Date();
      selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());

      const publishedAtISO = selectedDate.toISOString();
      formData.append("publishedAt", publishedAtISO);
    }
    const apiCall = data?._id
      ? WebService.fileUploadPutAPI({
          action: `media-list/edit-media/${data._id}`,
          body: formData,
        })
      : WebService.fileUploadAPI({
          action: `media-list/add-media`,
          body: formData,
        });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
        setPdfFile(null);
        setFile(null);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };
const pdfInputRef = useRef<HTMLInputElement | null>(null);
const handleRemovePdf = () => {
  if (pdfFile instanceof File) {
    URL.revokeObjectURL(URL.createObjectURL(pdfFile));
  }
  setPdfFile(null);
  if (pdfInputRef.current) {
    pdfInputRef.current.value = "";
  }
};
  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        setFile(null);
        setPdfFile(null);

        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{data ? "Edit News" : "Add News"}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>
            {/* Name */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Heading <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter heading"
                {...register("name", { required: "Heading is required" })}
              />
              {errors.name && (
                <div className="error-message">Please enter heading</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="slugField">
              <Form.Label className="label">
                Slug <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Auto-generated slug"
                {...register("slug", { required: "Slug is required" })}
                // disabled={!data?._id}
                // readOnly={!data?._id}
              />
              {errors.slug && (
                <div className="error-message">{errors.slug.message}</div>
              )}
            </Form.Group>
            {/* Link */}
            <Form.Group className="mb-2" controlId="eventLink">
              <Form.Label className="label">
                Link
                {/* <span className="text-danger">*</span> */}
              </Form.Label>
              <Form.Control
                type="url"
                placeholder="Enter link"
                {...register("link", {
                  pattern: {
                    value: /^(https?:\/\/)?([\w\-])+(\.[\w\-]+)+[/#?]?.*$/,
                    message: "Enter a valid URL",
                  },
                })}
              />
              {errors.link && (
                <div className="error-message">{errors.link.message}</div>
              )}
            </Form.Group>
            {/*Link Name */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Link Name
                {/* <span className="text-danger">*</span> */}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter link name"
                {...register("link_name")}
              />
              {errors.link_name && (
                <div className="error-message">{errors.link_name.message}</div>
              )}
            </Form.Group>
            {/* Description */}
            <Form.Group className="mb-2" controlId="eventDescription">
              <Form.Label className="label">
                Description <span className="text-danger">*</span>
              </Form.Label>

              <CommonQuill
                ref={quillRef}
                value={value}
                onChange={setEditorValue}
                storage="NEWS"
                initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
                onClearError={() =>
                  setCustomErrors((p) => ({
                    ...p,
                    description: undefined,
                  }))
                }
              />
              {customErrors.description && (
                <div className="error-message">{customErrors.description}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventDate">
              <Form.Label className="label">
                Published Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                {...register("publishedAt", { required: "Date is required" })}
              />
              {errors.publishedAt && (
                <div className="error-message">
                  {errors.publishedAt.message}
                </div>
              )}
            </Form.Group>
            {/* Tags Dropdown */}
            <Form.Group className="mb-2" controlId="eventTag">
              <Form.Label className="label">
                Tag <span className="text-danger">*</span>
              </Form.Label>

              <Form.Select
                {...register("tagId", { required: "Please select a tag" })}
                size="sm"
              >
                <option value="">Select Tag</option>
                {tagsList.map((tag: { _id: string; name: string }) => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))}
              </Form.Select>

              {errors.tagId && (
                <div className="error-message">{errors.tagId.message}</div>
              )}
            </Form.Group>
            {/* <Form.Group className="mt-2" controlId="isFeatured">
              <Form.Check
                type="checkbox"
                label="Featured"
                {...register("featured")}
              />
            </Form.Group> */}
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Media
            </span>
            <Row>
              <Col>
                {/* Image */}
                <Form.Group className="mb-2" controlId="eventImage">
                  <Form.Label className="label">
                    Image <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    className="custom-image-upload"
                    onChange={(e) => {
                      const input = e.target as HTMLInputElement;
                      const selectedFile = input.files?.[0];

                      if (!selectedFile) return;

                      if (!ALLOWED_IMAGE_TYPES.includes(selectedFile.type)) {
                        setFileError(
                          "Image format not supported. Please upload PNG, JPG, JPEG or SVG. image",
                        );
                        // console.log(fileError)
                        //      toast.error(fileError);
                        setFile(null);
                        input.value = ""; // reset input
                        return;
                      }

                      // ✅ valid
                      setFileError("");
                      setFile(selectedFile);
                    }}
                  />
                  {fileError && (
                    <p
                      className="text-danger mt-1"
                      style={{ fontSize: "13px" }}
                    >
                      {fileError}
                    </p>
                  )}
                  <span className="text-primary" style={{ fontSize: "12px" }}>
                    Note: Please upload an image of size 550×490 in .png, .jpeg,
                    .jpg or .svg format{" "}
                  </span>                  
                </Form.Group>
                {(file || data?.image) && (
                  <div className="mb-2 event-image">
                    <img
                      src={file ? URL.createObjectURL(file) : data?.image}
                      alt="Event"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
              </Col>
              <Col>
                <Form.Group className="mb-2" controlId="pdfFile">
                  <Form.Label className="label">
                    Upload PDF File
                  </Form.Label>
                  <Form.Control
                    type="file"
                    ref={pdfInputRef}
                    accept=".pdf"
                    className="custom-file-upload"
                    onChange={(e) => {
                      const input = e.target as HTMLInputElement;
                      const selectedFile = input.files && input.files[0];
                      if (selectedFile) {
                        setPdfFile(selectedFile);
                      }
                    }}
                  />
                  <span className="text-primary" style={{ fontSize: "12px" }}>
                    Note: Only PDF files are allowed. Maximum upload size is 100 MB
                    MB{" "}
                  </span>
                </Form.Group>
                {/* PDF Preview */}
                {pdfFile && (
  <div
    className="mb-2 event-file d-flex align-items-center justify-content-between"
    style={{
      background: "#f1f5f9",
      padding: "8px 12px",
      borderRadius: "8px",
    }}
  >
    <a
      href={
        pdfFile instanceof File
          ? URL.createObjectURL(pdfFile)
          : (pdfFile as string)
      }
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "none", fontWeight: 500 }}
    >
      📄 View PDF
    </a>

    <button
      type="button"
      onClick={handleRemovePdf}
      style={{
        background: "transparent",
        border: "none",
        color: "#dc2626",
        fontWeight: 700,
        cursor: "pointer",
        fontSize: "16px",
      }}
      title="Remove file"
    >
      ✕
    </button>
  </div>
)}
              </Col>
            </Row>
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              SEO
            </span>

            <Form.Label className="label">
              Meta Description <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Meta Description"
              {...register("meta_description", {
                required: "Meta description is required",
              })}
            />
            {errors.meta_description && (
              <div className="error-message">
                {errors.meta_description.message}
              </div>
            )}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Keywords <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter keywords"
                {...register("keywords", { required: "Keywords are required" })}
              />
              {errors.keywords && (
                <div className="error-message">{errors.keywords.message}</div>
              )}
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>
      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddMediaList;
